import { PDFExport } from "@progress/kendo-react-pdf";
import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";

import { DocketDoc } from "../components/AddWorkDocketModal";
import VoidedRemarksForm from "../components/VoidedRemarksForm";
import DocketPrintPreviewDataTable from "../components/print/DocketPrintPreviewDataTable";

import { createBase64Data } from "@/components/print-format/PrintFormat";
import HidePrintElement from "@/components/reusables/HidePrintElement";
import { Action_Type, Button_Names } from "@/constants/global-constants";
import { WorkDocketTab } from "@/constants/tabs.constant";
import {
  WorkDocketErrorMessages,
  WorkDocketStatus,
  WorkDocketToastMessages,
} from "@/constants/work-docket.constant";
import { useGetParams } from "@/hooks/useGetParams";
import {
  PaginationMeta,
  ResponsePagination,
  usePagination,
} from "@/hooks/usePagination";
import { QueryRestType } from "@/interfaces/interfaces";
import { IProject } from "@/interfaces/project.interface";
import { WasteFormat } from "@/interfaces/wastetype.interface";
import { Docket } from "@/interfaces/workdocket.interface";
import { dataUrlToBlob } from "@/lib/file-image.utils";
import Overlay from "@/pages/waste-management/summary/components/overlay";
import {
  ExtendedGeneralResponse,
  useGetDocketQuery,
  useMutateStatusMutation,
} from "@/redux/api/docketQuery";
import * as projectsQuery from "@/redux/api/projectsQuery";
import { useGetWasteFormatQuery } from "@/redux/api/wasteManageSummaryandSettings";
import { showAlertDialog } from "@/redux/slices/AlertSlice";
import { TUpdateDocketReport } from "@/schemas/work-docket.schema";
import { customToast } from "@/utils/custom-toast";
import {
  parseToFormData,
  validateResponse,
} from "@/utils/helper-functions/form.helper";

interface ResType {
  data?: Docket[];
  pagination: PaginationMeta;
  lastDocketIndex: string;
  rest: QueryRestType;
}

interface WorkDocketContextTypes {
  isShowAddWorkDocket: boolean;
  setIsShowAddWorkDocket: React.Dispatch<React.SetStateAction<boolean>>;
  isShowEditWorkDocket: boolean;
  setIsShowEditWorkDocket: React.Dispatch<React.SetStateAction<boolean>>;
  isShowDocketSheet: boolean;
  setIshowDocketSheet: React.Dispatch<React.SetStateAction<boolean>>;
  isShowDocketHistory: boolean;
  setIshowDocketHistory: React.Dispatch<React.SetStateAction<boolean>>;
  isResolveWd: boolean;
  setIsResolveWd: React.Dispatch<React.SetStateAction<boolean>>;
  pagination: ResponsePagination;
  res: ResType;
  letterHead: WasteFormat;
  handleSetPrintData: (changedData: TUpdateDocketReport) => void;
  componentRef: React.RefObject<HTMLDivElement>;
  handleStatusChange: (status: WorkDocketStatus, id: string) => Promise<void>;
  projects: GeneralResponse<IProject[]> | undefined;
}

export const WorkDocketContext = createContext<WorkDocketContextTypes>(
  {} as WorkDocketContextTypes,
);

export const useWorkDocketContext = (): WorkDocketContextTypes => {
  return useContext(WorkDocketContext);
};

export const WorkDocketContextProvider = ({
  children,
  isReport,
}: {
  children: React.JSX.Element;
  isReport: boolean;
}): React.JSX.Element => {
  const filter = useGetParams(
    [
      "projectId",
      "customerId",
      "dateRange",
      "search",
      "limit",
      "offset",
      "currentPage",
      "status",
      ...(isReport ? ["sortBy", "orderBy"] : []),
    ],
    {
      status: WorkDocketTab.at(0)?.value ?? "",
    },
  );
  const [isShowAddWorkDocket, setIsShowAddWorkDocket] = useState(false);
  const [isShowEditWorkDocket, setIsShowEditWorkDocket] = useState(false);
  const [isResolveWd, setIsResolveWd] = useState(false);
  const [isShowDocketSheet, setIshowDocketSheet] = useState(false);
  const [isShowDocketHistory, setIshowDocketHistory] = useState(false);
  const [_, setVoidedRemarks] = useState("");
  const [shouldGeneratePdf, setShouldGeneratePdf] = useState<{
    status: string;
    id: string;
  } | null>(null);

  const [mutateStatus] = useMutateStatusMutation();
  const { data, ...rest } = useGetDocketQuery(
    {
      filter: filter?.substring(1),
    },
    {
      skip: !filter.includes("limit="),
    },
  );

  const res = useMemo(() => {
    const docketData = data as ExtendedGeneralResponse<Docket[]>;

    return {
      data: docketData?.data,
      pagination: docketData?.pagination,
      lastDocketIndex: docketData?.lastDocketIndex,
      rest,
    };
  }, [data, rest]);

  const pagination = usePagination(res?.pagination);

  const { data: projects } = projectsQuery.useGetProjectsByStatusQuery({
    status: "",
  });

  // for pdf data
  const pdfExportComponent = useRef<PDFExport>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const { data: wasteRes } = useGetWasteFormatQuery({});
  const letterHead = wasteRes as WasteFormat;

  const [printData, setPrintData] = useState<DocketDoc | null>();

  const handleSetPrintData = (changedData: TUpdateDocketReport): void => {
    const projectData = projects?.data?.find(
      (proj) => proj.id === changedData.projectId,
    );
    setPrintData({
      ...changedData,
      ...(changedData?.isProjectManagerOnSite && {
        signature: changedData?.signature,
      }),
      docketId: changedData?.docketId ?? res?.lastDocketIndex ?? "0",
      customer: projectData?.customer,
      project: projectData,
      createdAt: new Date().toISOString(),
    } as DocketDoc);
  };

  const dispatch = useDispatch();

  const handleAddRemarks = (formData: FormData): Promise<boolean> => {
    let tempRemarks = "";

    return new Promise((resolve) => {
      dispatch(
        showAlertDialog({
          node: {
            node: (
              <VoidedRemarksForm
                setVoidedRemarks={(value) => {
                  tempRemarks = value;
                  setVoidedRemarks(value);
                }}
              />
            ),
            className: "lg:min-w-[600px]",
          },
          cancelName: Button_Names.Cancel,
          btnName: Action_Type.Add,
          btnAction: () => {
            formData.append("remarks", tempRemarks);
            resolve(true);
          },
          onClose: () => {
            resolve(false);
          },
          footerElementAttr: {
            className: "flex flex-row max-w-[200px]",
            order: "last",
          },
        }),
      );
    });
  };

  // Add useEffect to handle PDF generation
  React.useEffect(() => {
    if (shouldGeneratePdf && printData) {
      const generateAndSendPdf = async (): Promise<void> => {
        try {
          const base64 = await createBase64Data({
            componentRef,
            letterHead,
          });

          // Prepare form data again
          const singleData = res?.data?.find(
            (d) => d.id === shouldGeneratePdf.id,
          );
          if (!singleData) {
            return;
          }

          const formData = parseToFormData(
            { ...singleData, status: shouldGeneratePdf.status },
            ["images", "disputeImage", "signature"],
            { status: true },
          );

          formData.delete("status");
          formData.append("status", shouldGeneratePdf.status);

          const pdfBlob = dataUrlToBlob(base64, "application/pdf");
          formData.append("file", pdfBlob);

          const response = await mutateStatus({
            id: shouldGeneratePdf.id,
            data: formData,
          });

          validateResponse({
            response,
            onSuccess: () => {
              customToast.success(WorkDocketToastMessages.WD_STATUS_UPDATED);
            },
          });
        } catch (error) {
          customToast.error(WorkDocketErrorMessages.PDF_GENERATION_FAILED);
        } finally {
          setShouldGeneratePdf(null);
        }
      };

      generateAndSendPdf().catch(() =>
        customToast.error(WorkDocketErrorMessages.STATUS_CHANGED_FAILED),
      );
    }
  }, [shouldGeneratePdf, printData]);

  const handleStatusChange = async (
    status: WorkDocketStatus,
    id: string,
  ): Promise<void> => {
    if (!status || !id) {
      return;
    }

    // Find the single data object
    const singleData = res?.data?.find((d) => d.id === id);

    if (singleData) {
      // Create a new copy of the object with the updated status
      const singleDataCopy = { ...singleData, status };

      // Use the copied object with the updated status
      handleSetPrintData({
        ...singleData,
        status,
      } as TUpdateDocketReport);

      // Prepare form data
      const formData = parseToFormData(
        singleDataCopy,
        ["images", "disputeImage", "signature"],
        {
          status: true,
        },
      );
      setVoidedRemarks("");
      if (status === WorkDocketStatus.VOIDED) {
        const continueProcess = await handleAddRemarks(formData);

        // If user cancelled the dialog, stop the process
        if (!continueProcess) {
          return;
        }
      }

      formData.delete("status");
      formData.append("status", status);
      setShouldGeneratePdf({ status, id });
    }
  };

  return (
    <WorkDocketContext.Provider
      value={{
        isShowAddWorkDocket,
        setIsShowAddWorkDocket,
        isShowEditWorkDocket,
        setIsShowEditWorkDocket,
        isShowDocketSheet,
        setIshowDocketSheet,
        isShowDocketHistory,
        setIshowDocketHistory,
        isResolveWd,
        setIsResolveWd,
        pagination,
        res,
        letterHead,
        handleSetPrintData,
        componentRef,
        handleStatusChange,
        projects,
      }}
    >
      {children}
      <HidePrintElement>
        <PDFExport
          ref={pdfExportComponent}
          forcePageBreak='.page-break' //Use .page-break class to force page break
          paperSize='A4'
          fileName='estimate-review.pdf'
          pageTemplate={() => <Overlay />}
          scale={0.7} // scale down the content to fit the page
          margin={"1cm"}
          keepTogether='section'
        >
          <div ref={componentRef}>
            <DocketPrintPreviewDataTable
              data={(printData ?? {}) as Docket}
              isExport={true}
            />
          </div>
        </PDFExport>
      </HidePrintElement>
    </WorkDocketContext.Provider>
  );
};
